import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';
import { BarChart } from '@mui/icons-material';

const EXPENSE_COLORS = {
    housing: '#2196f3',
    food: '#673ab7',
    healthcare: '#4caf50',
    insurance: '#ff5722',
    personal: '#795548',
    utilities: '#009688',
    education: '#3f51b5',
    entertainment: '#e91e63',
    transportation: '#ffc107',
    other: '#9e9e9e',
};

const determineExpenseCategory = (expenseName) => {
    const name = (expenseName || '').toLowerCase();
    if (name.match(/rent|mortgage|property|home|house|apartment|condo|maintenance|repair|renovation|lawn|garden|cleaning service|pest control/)) return 'housing';
    if (name.match(/food|grocery|groceries|restaurant|dining|meal|breakfast|lunch|dinner|snack|coffee|beverage/)) return 'food';
    if (name.match(/health|medical|doctor|dental|vision|prescription|therapy|counseling|medication|hospital|clinic|wellness/)) return 'healthcare';
    if (name.match(/insurance|coverage|policy|premium/)) return 'insurance';
    if (name.match(/personal|clothing|hair|beauty|gym|fitness|hobby|gift|donation|charity|salon|spa/)) return 'personal';
    if (name.match(/utility|utilities|electric|water|gas|power|internet|phone|cable|tv|garbage|sewage/)) return 'utilities';
    if (name.match(/education|school|college|university|tuition|course|class|training|book|supply|student/)) return 'education';
    if (name.match(/entertainment|movie|game|toy|sport|concert|theater|music|streaming|subscription|recreation|vacation|travel/)) return 'entertainment';
    if (name.match(/transport|car|vehicle|gas|fuel|bus|train|subway|taxi|uber|lyft|parking|maintenance|repair|transit/)) return 'transportation';
    return 'other';
};

const DONUT_SIZE = 80;
const DONUT_THICKNESS = 20;

const ExpenseSummaryCard = ({
    expenses = [],
    expenseTypes = {},
}) => {
    const { isDarkMode } = useTheme();
    const [isHovering, setIsHovering] = useState(false);

    // Group by category
    const chartData = React.useMemo(() => {
        const byCategory = {};
        for (const expense of expenses) {
            const category = determineExpenseCategory(expense.expense_type_name);
            const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
            const amount = parseFloat(expense.amount || 0) * (expense.frequency_multiplier || 1);
            if (!byCategory[category]) {
                byCategory[category] = {
                    name: categoryLabel,
                    value: 0,
                    color: EXPENSE_COLORS[category],
                };
            }
            byCategory[category].value += amount;
        }
        return Object.values(byCategory).sort((a, b) => b.value - a.value);
    }, [expenses]);

    const totalAnnual = chartData.reduce((sum, item) => sum + item.value, 0);

    if (!expenses.length) {
        return (
            <div className="p-2 text-center text-gray-400 dark:text-gray-500 text-sm">
                No expense data available
            </div>
        );
    }

    return (
        <div
            className="relative p-0"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {/* Tooltip (green bubble, top right) */}
            <div className="absolute top-2 right-4 z-10 flex flex-col items-end pointer-events-none">
                {isHovering && (
                    <div className="relative">
                        <div className="bg-green-600 text-white text-xs font-semibold rounded-md px-3 py-1 shadow-lg">
                            View/Edit Expense Details
                        </div>
                        <div className="absolute left-1/2 -bottom-2 transform -translate-x-1/2 w-3 h-3 overflow-hidden">
                            <svg className="w-3 h-3" viewBox="0 0 12 12">
                                <polygon points="6,12 12,0 0,0" fill="#16a34a" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
            {/* Main Content - Stacked Layout */}
            <div className="flex flex-col p-0">
                {/* Title with icon */}
                <div className="flex items-center gap-1 mb-2 px-2">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Household Expense Analysis</h2>
                    <BarChart className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                {/* Donut Chart - Centered */}
                <div className="flex w-full justify-center mb-3 mt-2">
                    <div className="relative w-[80px] h-[80px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={DONUT_SIZE / 2 - DONUT_THICKNESS}
                                    outerRadius={DONUT_SIZE / 2}
                                    paddingAngle={1}
                                    dataKey="value"
                                    startAngle={90}
                                    endAngle={-270}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color}
                                            fillOpacity={0.85}
                                            stroke={isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)'}
                                            strokeWidth={1}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center Label */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                            <div className={`text-[10px] block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total</div>
                            <div className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalAnnual)}</div>
                        </div>
                    </div>
                </div>
                {/* Table */}
                <div className="w-full overflow-x-auto">
                    <table className="w-full table-auto">
                        <thead>
                            <tr>
                                <th className="text-xs font-semibold py-0.5 px-1 text-left text-gray-500 dark:text-gray-300">Category</th>
                                <th className="text-xs font-semibold py-0.5 px-1 text-right text-gray-500 dark:text-gray-300">%</th>
                                <th className="text-xs font-semibold py-0.5 px-1 text-right text-gray-500 dark:text-gray-300">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chartData.map((item) => (
                                <tr
                                    key={item.name}
                                    className={`
                  border-b last:border-b-0 transition-colors duration-150
                  ${isDarkMode
                                        ? 'border-neutral-700 hover:bg-neutral-800'
                                        : 'border-gray-100 hover:bg-gray-50'
                                    }
                `}
                                >
                                    <td className="py-0.5 px-1">
                                        <span
                                            className="px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                                            style={{
                                                backgroundColor: item.color,
                                                opacity: 0.85
                                            }}
                                        >
                                            {item.name}
                                        </span>
                                    </td>
                                    <td className="py-0.5 px-1 text-right text-xs font-medium text-gray-900 dark:text-white">
                                        {totalAnnual === 0 ? '0.0' : ((item.value / totalAnnual) * 100).toFixed(1)}%
                                    </td>
                                    <td className="py-0.5 px-1 text-right text-xs font-medium text-gray-900 dark:text-white">
                                        {formatCurrency(item.value)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ExpenseSummaryCard;
